import { getAdminOrgId } from "./admin";
import { getApiBase, getAuthHeaders } from "./auth";

const API_BASE = getApiBase();

function defaultHeaders(orgId: string): Record<string, string> {
  return getAuthHeaders({ orgId, json: true });
}

export type TaskOpsStatus = "not_started" | "in_progress" | "blocked" | "completed";
export type TaskLifecycleStatus = "active" | "paused" | "archived" | "cancelled";
export type TaskFinancialStatus = "unpaid" | "partial" | "paid";
export type TaskHealthState = "good" | "attention" | "critical";
export type StepInstanceStatus = "pending" | "active" | "completed" | "skipped";
export type ActivityInstanceStatus = "pending" | "done";
export type TaskEventType = "created" | "reset" | "assigned" | "status_changed" | "step_started" | "step_completed" | "activity_completed" | "note_added";

export type TaskInstance = {
  id: string;
  org_id: string;
  client_org_id: string;
  process_version_id: string;
  source_invoice_id: string;
  ops_family: string;
  status_ops: TaskOpsStatus;
  status_lifecycle: TaskLifecycleStatus;
  status_financial: TaskFinancialStatus;
  health_state: TaskHealthState;
  assigned_to_user_id: string | null;
  start_date: string;
  target_end_date: string | null;
  reset_count: number;
  last_reset_at: string | null;
  reset_of_task_id: string | null;
  created_at: string;
  updated_at: string;
};

export type TaskStepInstance = {
  id: string;
  task_id: string;
  step_template_id: string;
  order_index: number;
  status: StepInstanceStatus;
  started_at: string | null;
  due_at: string | null;
  completed_at: string | null;
  lifecycle_status: "active" | "archived";
  created_at: string;
  updated_at: string;
};

export type TaskActivityInstance = {
  id: string;
  step_instance_id: string;
  activity_template_id: string;
  status: ActivityInstanceStatus;
  completed_at: string | null;
  completed_by_user_id: string | null;
  note: string | null;
  lifecycle_status: "active" | "archived";
  created_at: string;
  updated_at: string;
};

export type TaskEvent = {
  id: string;
  task_id: string;
  event_type: TaskEventType;
  actor_user_id: string | null;
  at: string;
  payload: any | null;
};

export type StepActivityTemplateSummary = {
  id: string;
  title: string;
  activity_type: string;
  checklist_config: { email_template_code?: string | null; email_recipient_type?: string } | null;
  activity_ui_config?: {
    fields?: Array<{ id: string; label: string; type: string; required?: boolean; placeholder?: string | null; choices?: Array<{ value: string; label: string }> | null; default_value?: string | number | boolean | null; presentation?: "dropdown" | "radio" }>;
    conditional_rules?: Array<{ source_field_id: string; operator: string; value?: unknown; target_field_id: string; action: string }>;
    action_rules?: Array<{ source_field_id: string; operator: string; value?: unknown; actions: Array<{ type: string; params: Record<string, unknown> }> }>;
    buttons?: Array<{ id: string; label: string; action_type: string; action_params: Record<string, unknown>; visibility_condition?: { source_field_id: string; operator: string; value?: unknown } | null; context_binding?: string | null }>;
  } | null;
};

export type StepActivityTemplatesForStep = {
  step_instance_id: string;
  activities: StepActivityTemplateSummary[];
};

export type StepActivityInstancesForStep = {
  step_instance_id: string;
  activities: TaskActivityInstance[];
};

export type TaskDetail = TaskInstance & {
  step_instances: TaskStepInstance[];
  step_activity_templates?: StepActivityTemplatesForStep[];
  step_activity_instances?: StepActivityInstancesForStep[];
  events: TaskEvent[];
};

export type TaskInstanceUpdate = {
  status_ops?: TaskOpsStatus;
  status_lifecycle?: TaskLifecycleStatus;
  status_financial?: TaskFinancialStatus;
  health_state?: TaskHealthState;
  assigned_to_user_id?: string | null;
};

export type CompleteStepRequest = {
  note?: string | null;
};

export type CompleteActivityRequest = {
  note?: string | null;
  field_values?: Record<string, string | number | boolean> | null;
  triggered_button_id?: string | null;
};

export type ResetTaskRequest = {
  reset_type?: "soft" | "hard";
  note?: string | null;
};

export type PagedTasks = {
  items: TaskInstance[];
  meta: {
    total: number;
    page: number;
    page_size: number;
  };
};

export type PagedTaskEvents = {
  items: TaskEvent[];
  meta: {
    total: number;
    page: number;
    page_size: number;
  };
};

export async function listTasks(params: {
  client_org_id?: string;
  status_ops?: string;
  status_lifecycle?: string;
  health_state?: string;
  page?: number;
  page_size?: number;
}): Promise<PagedTasks> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params.client_org_id) queryParams.append("client_org_id", params.client_org_id);
  if (params.status_ops) queryParams.append("status_ops", params.status_ops);
  if (params.status_lifecycle) queryParams.append("status_lifecycle", params.status_lifecycle);
  if (params.health_state) queryParams.append("health_state", params.health_state);
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.page_size) queryParams.append("page_size", params.page_size.toString());

  const response = await fetch(`${API_BASE}/ops/tasks?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load tasks");
  }
  return response.json();
}

export async function getTask(taskId: string): Promise<TaskDetail> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/ops/tasks/${taskId}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load task");
  }
  return response.json();
}

export async function getTaskEvents(
  taskId: string,
  params: { page?: number; page_size?: number }
): Promise<PagedTaskEvents> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.page_size) queryParams.append("page_size", params.page_size.toString());

  const response = await fetch(`${API_BASE}/ops/tasks/${taskId}/events?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load task events");
  }
  return response.json();
}

export async function updateTask(taskId: string, payload: TaskInstanceUpdate): Promise<TaskInstance> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/ops/tasks/${taskId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to update task");
  }
  return response.json();
}

export async function completeStep(
  taskId: string,
  stepInstanceId: string,
  payload: CompleteStepRequest = {}
): Promise<TaskStepInstance> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/ops/tasks/${taskId}/steps/${stepInstanceId}/complete`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to complete step");
  }
  return response.json();
}

export async function completeActivity(
  activityInstanceId: string,
  payload: CompleteActivityRequest = {}
): Promise<TaskActivityInstance> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/ops/tasks/activities/${activityInstanceId}/complete`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to complete activity");
  }
  return response.json();
}

export async function resetTask(taskId: string, payload: ResetTaskRequest = {}): Promise<TaskInstance> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/ops/tasks/${taskId}/reset`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to reset task");
  }
  return response.json();
}

export type CostLineOut = {
  id: string;
  task_instance_id: string;
  organization_id: string;
  invoice_line_id: string | null;
  cost_type: string;
  source: string;
  supplier_product_id: string | null;
  label: string;
  quantity: number;
  unit_cost: number;
  currency: string;
  total_amount: number;
  activity_instance_id: string | null;
  created_at: string;
  updated_at: string;
};

export type InvoiceLineSummary = {
  id: string;
  label: string;
  line_order: number;
};

export type OperationalDocumentSummary = {
  task_id: string;
  total_revenue: number;
  total_costs: number;
  margin: number;
  cost_lines: CostLineOut[];
  currency_warning?: string | null;
  invoice_lines?: InvoiceLineSummary[];
};

export async function getOperationalDocument(taskId: string): Promise<OperationalDocumentSummary> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/ops/tasks/${taskId}/operational-document`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load operational document");
  }
  return response.json();
}

export async function updateTaskCostLine(
  taskId: string,
  costLineId: string,
  payload: { invoice_line_id?: string | null; label?: string; quantity?: number; unit_cost?: number; total_amount?: number; currency?: string }
): Promise<CostLineOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/ops/tasks/${taskId}/cost-lines/${costLineId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update cost line");
  }
  return response.json();
}
